import { html, PolymerElement } from "@polymer/polymer/polymer-element.js";

/**
 * `web-socket`
 * An element to ease usage of WebSockets.
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */

class SocketIo extends PolymerElement {
  static get template() {
    return html`
      <style>
        :host {
          display: none;
        }
      </style>
    `;
  }

  static get properties() {
    return {
      /**
       * Connect to this server and namespace.
       *
       * Leave `undefined` to use socket.io's default. See
       * [socket.io's client API](http://socket.io/docs/client-api/#io%28url:string,-opts:object%29:socket)
       * for more information.
       *
       */
      url: {
        type: String,
        value: undefined,
      },

      /**
       * Events listed here will be forwarded into Polymer's event system.
       *
       * Implies `autoForwarding=false`.
       */
      forwarding: {
        type: Array,
        value: function () {
          return [];
        },
      },

      /**
       * List of received event types.
       *
       */
      eventsDetected: {
        type: Array,
        value: function () {
          return [];
        },
        readOnly: true,
        notify: true,
      },

      /**
       * Automatically connect to the Socket.IO server.
       *
       */
      autoConnecting: {
        type: Boolean,
        value: false,
      },

      /**
       * Automatically forward events to Polymer.
       *
       */
      autoForwarding: {
        type: Boolean,
        value: false,
      },

      _autoForwarding: {
        type: Boolean,
        computed: "_computeAutoForwarding(autoForwarding, forwarding)",
      },

      connected: {
        type: Boolean,
        value: false,
        readOnly: true,
        notify: true,
      },

      isLogin: {
        type: Number,
        value: 0
      }
    };
  }

  static get observers() {
    return ["_subscribeForwarding(forwarding, connected)"];
  }

  // Lifecycle Events

  ready() {
    super.ready();
    // console.log("socket-io attached");
    this._io_listeners = [];
    if (this.autoConnecting) {
      this.connect();
    }
  }

  /**
   * Send a message to the Socket.IO server.
   */
  send(event, message) {
    this.socket.emit(event, message);
  }

  /**
   * Connect to the Socket.IO server.
   */
  connect() {
    if (!this.connected) {
      this.socket = io(this.url, { transports: ["polling"] });
      // console.log("Socket is connected");
      // The following line accesses a method that is not, strictly
      // speaking, part of the Socket.IO API. If this component stops
      // working, then it's probably worth checking here and seeing if
      // Socket.IO changed something.
      //
      // This event is emitted by the manager when an incoming packet is
      // done being decoded by engine.io-parser.
      //
      // Yes, 'an incoming packet' means 'all the packets' (engine.io
      // packets, and not, for example, TCP/IP packets).
      //
      // Time to drink the firehose!
      this.socket.io.on("packet", this._onPacket.bind(this));
      this._setConnected(true);
      this.dispatchEvent(new CustomEvent("connection", {}));
      this.setAutoSubscribe();
    }
  }

  isConnected() {
    if (this.socket.connected) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Disconnect from the Socket.IO server.
   */
  disconnect() {
    if (this.connected) {
      this._subscribeForwarding();
      this.socket.disconnect();
      this._setConnected(false);
      this.dispatchEvent(new CustomEvent("disconnection", {}));
    }
  }

  /**
   * Set Auto subscribe room.
   */
  setAutoSubscribe() {
    this.socket.on("connect", function () {
        // console.log("client connect");
        if(this.isLogin) {
          this.send("subscribe", { uid: this.uid, room_id: this.roomId });
          this.dispatchEvent(new CustomEvent("status-connect", {}));
        }
      }.bind(this)
    );
    this.socket.on("reconnect", function () {
      this.dispatchEvent(new CustomEvent("status-reconnect", {})); // listener checking connected
      // console.log("client reconnect");
    }.bind(this));
    this.socket.on("disconnect", function () {
      this.dispatchEvent(new CustomEvent("status-disconnect", {})); // listener checking connected
      // console.log("client disconnect");
    }.bind(this));
  }

  /**
   * Subscribe to events listed as forwarding into Polymer.
   */
  _subscribeForwarding(forwarding, connected) {
    if (connected) {
      forwarding.forEach(function (el) {
        // dasherize here to avoid repeating it later.
        var dasherizedName = el.replace(/[^\w]+/g, "-").toLowerCase();
        var callback = this._handleMessage.bind(this, dasherizedName);
        this.socket.on(el, callback);

        // save listener for later removal
        this._io_listeners.push([el, callback]);
      }, this);
    }
  }

  /**
   * Unsubscribe from events listed as forwarding into Polymer.
   */
  _unsubscribeForwarding() {
    this._io_listeners.forEach(function (el) {
      var eventName = el[0];
      var callback = el[1];

      this.socket.off(eventName, callback);
    }, this);

    // clear listeners
    this._io_listeners = [];
  }

  /**
   * "`forwarding` implies `autoForwarding=false`"
   */
  _computeAutoForwarding(autoForwarding, forwarding) {
    return autoForwarding && forwarding.length < 1;
  }

  /**
   * Generic event handler.
   *
   * Forwards event into Polymer's event system.
   */
  _handleMessage(name, data) {
    // prefix the event to avoid collisions with other events this
    // component fires, or in the future might need to fire.
    this.dispatchEvent(new CustomEvent(`io-${name}`, {detail: data}));
    // console.log(name, data);
  }

  /**
   * Drinking the firehose.
   *
   * WARNING: Digs into Socket.IO internals.
   *
   * TODO: Replace with an `addGlobalListener()` or `on('*', [...])`
   *       when socket-io adds support for either of those.
   */
  _onPacket(packet) {
    // We don't have access to the following constants, but we still
    // need to filter for them:
    var /*engine.io-parser*/ EVENT = 2;
    var /*engine.io-parser*/ BINARY_EVENT = 5;

    // This is basically what socket.io's `Socket.onPacket()` and
    // Socket.onevent()` methods do, except we don't care about any of
    // the other types and we're ignoring message buffers and hoping it
    // just works.
    if (packet.type === EVENT || packet.type === BINARY_EVENT) {
      var eventName = packet.data[0];
      var eventData = packet.data[1];

      // append new event types to detected array
      if (!this.eventsDetected.includes(eventName)) {
        this.push("eventsDetected", eventName);
      }

      if (this._autoForwarding) {
        this._handleMessage(eventName, eventData);
      }
    }
  }
}

window.customElements.define("socket-io", SocketIo);
